// @flow

import React, { type Node } from 'react';
import styled, { css } from 'styled-components';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { TextField, Link } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { getZipcodeNormalizer } from '@tectonics/prs/customer/normalize';

import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';

import spinnerSrc from './spinner.svg';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

const zipcodeNormalizer = getZipcodeNormalizer('br');

type Props = {
  isSearching: bool,
};

export function FieldBrazilianZipcode({ isSearching }: Props): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextFieldWithFormatting(
    'brazilianZipcode',
    formatRaw,
    zipcodeNormalizer,
  );

  const validationError = useUserInputValidationError('brazilianZipcode');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('zipcode');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="address-zipcode">
      <LabelWrapper>
        <label htmlFor={inputId}>
          CEP
        </label>
        <Link
          href="http://www.buscacep.correios.com.br/sistemas/buscacep/"
          target="_blank" rel="noreferrer noopener">
          Não sei meu CEP
        </Link>
      </LabelWrapper>

      <TextFieldWrapper>
        <TextField
          as={InputMask}
          id={inputId}
          type="tel"
          mask={CEP_MASK}
          value={value}
          onChange={handleChange}
          fashion={formHasBeenSent && validationError ? 'error' : null}
          readOnly={submitting}
          data-error={formHasBeenSent && validationError ? 'error' : null}
          name="address-zipcode"
        />

        <SpinnerImg isVisible={isSearching} alt="loading indicator"/>
      </TextFieldWrapper>

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

export const LabelWrapper = styled.div`
  display: flex;
  label {
    flex-grow: 1;
  }
`;

const TextFieldWrapper = styled.div`
  position: relative;
`;

const SpinnerImg = styled.img.attrs({ src: spinnerSrc })`
  position: absolute;
  right: 8px;
  top: 50%;
  margin-top: -12px;
  height: 24px;
  width: auto;
  display: none;

  ${({ isVisible }) => isVisible && css`
    display: block;
  `}
`;

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          Informe o seu CEP
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          CEP inválido
        </ErrorLabel>
      );
  }
}

const CEP_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-',
  /[0-9]/, /[0-9]/, /[0-9]/,
];

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, CEP_MASK);
}
